/**
 * Explorateur des projets soutenus — mise en page « Vitrine » de la page
 * publique d'une entreprise, À PARTIR DE 5 PROJETS.
 *
 * Demande de Guillaume (01/10/2026) : « Dans les pages publiques, je ne veux
 * pas quelque chose qui défile à l'infini avec les projets. » ICADE soutient
 * près de 70 projets : 70 sections à la suite ne se lisent pas. Décision :
 *  — 4 projets ou moins : l'affichage actuel (une section par projet) ;
 *  — 5 projets et plus : un explorateur (liste filtrable à gauche, détail
 *    complet d'UN projet à droite, précédent / suivant, carte au-dessus) ;
 *  — 10 projets et plus : une synthèse par famille au-dessus de la liste
 *    (« 38 exploitations agricoles · 28 forêts · 1 verger · 1 projet de
 *    haies »), chaque famille servant de filtre.
 *
 * Module PUR et léger (aucun import de valeur) : il sert au rendu serveur de
 * la page, au composant client de l'explorateur et aux tests
 * (scripts/tests/page-publique-vitrine.test.ts).
 */
import type { ProjetPublic } from './contenu-page-publique'

/* ═══════ Seuils ═══════ */

/** À partir de ce nombre de projets, la Vitrine passe à l'explorateur. */
export const SEUIL_EXPLORATEUR = 5
/** À partir de ce nombre de projets, la synthèse par famille paraît au-dessus de la liste. */
export const SEUIL_SYNTHESE = 10

export const avecExplorateur = (nombreProjets: number) => nombreProjets >= SEUIL_EXPLORATEUR
export const avecSynthese = (nombreProjets: number) => nombreProjets >= SEUIL_SYNTHESE

/* ═══════ Familles ═══════ */

/** Mêmes clés que `Famille` (src/lib/espace/projet-affichage.ts). */
export type FamilleExplorateur = 'foret' | 'gestion' | 'agriculture' | 'verger' | 'haies' | 'batiment'

const NOMS_FAMILLE: Record<FamilleExplorateur, { un: string; plusieurs: string; filtre: string }> = {
  agriculture: { un: 'exploitation agricole', plusieurs: 'exploitations agricoles', filtre: 'Agriculture' },
  foret: { un: 'forêt', plusieurs: 'forêts', filtre: 'Forêt' },
  gestion: { un: 'forêt en gestion continue', plusieurs: 'forêts en gestion continue', filtre: 'Gestion forestière' },
  verger: { un: 'verger', plusieurs: 'vergers', filtre: 'Vergers' },
  haies: { un: 'projet de haies', plusieurs: 'projets de haies', filtre: 'Haies' },
  batiment: { un: 'projet bâtiment bois', plusieurs: 'projets bâtiment bois', filtre: 'Bâtiment bois' },
}

/** Couleur d'une famille sur la carte et dans la synthèse (pastille de légende). */
export const COULEUR_FAMILLE: Record<FamilleExplorateur, string> = {
  foret: '#1B4D3E',
  gestion: '#2D7D5F',
  agriculture: '#C28A2C',
  verger: '#B4452F',
  haies: '#5C8A3A',
  batiment: '#4A90A4',
}

const estFamille = (v: string): v is FamilleExplorateur => v in NOMS_FAMILLE
const famille = (v: string): FamilleExplorateur => (estFamille(v) ? v : 'foret')

/** « 1 forêt », « 28 forêts », « 38 exploitations agricoles ». */
export function libelleFamille(f: FamilleExplorateur, n: number): string {
  const noms = NOMS_FAMILLE[f]
  return `${n.toLocaleString('fr-FR')} ${n > 1 ? noms.plusieurs : noms.un}`
}

/* ═══════ Éléments de la liste ═══════ */

/**
 * Un projet tel que la liste de l'explorateur le montre et le filtre :
 * jamais de prix, de porteur, d'exploitation ni d'autre financeur — seulement
 * le nom public, le lieu, la méthode et les tonnes de l'entreprise.
 */
export interface ProjetExplorateur {
  slug: string
  titre: string
  commune: string | null
  /** « Maine-et-Loire (49) », ou null. */
  departement: string | null
  region: string
  famille: FamilleExplorateur
  /** Clé de la méthode (type du catalogue : `boisement`, `carbon_agri`…). */
  type: string
  /** « Boisement », « Grandes cultures »… */
  methode: string
  tonnes: number | null
  annee: number | null
  enAvant: boolean
  coords: { lat: number; lon: number } | null
  /** Photo du projet (vignettes, cartes des familles), ou null. */
  photo: { url: string; alt: string } | null
}

export function elementsExplorateur(projets: ProjetPublic[]): ProjetExplorateur[] {
  return projets.map((p) => ({
    slug: p.slug,
    titre: p.titre,
    commune: p.commune,
    departement: p.departement,
    region: p.region,
    famille: famille(p.famille),
    type: p.type,
    methode: p.typeLibelle,
    tonnes: p.tonnes,
    annee: p.annee,
    enAvant: p.enAvant,
    coords: p.coords,
    photo: p.photo ? { url: p.photo.url, alt: p.photo.alt } : null,
  }))
}

/* ═══════ Synthèse par famille ═══════ */

export interface SyntheseFamille {
  famille: FamilleExplorateur
  nombre: number
  tonnes: number
  /** « 38 exploitations agricoles » */
  libelle: string
  couleur: string
  /** Photo du plus gros projet de la famille qui en a une (photo différente de celle des autres familles), ou null. */
  photo: { url: string; alt: string } | null
}

/** Familles par nombre de projets décroissant ; vide sous le seuil de la synthèse. */
export function syntheseFamilles(items: ProjetExplorateur[]): SyntheseFamille[] {
  if (!avecSynthese(items.length)) return []
  const par = new Map<FamilleExplorateur, { nombre: number; tonnes: number }>()
  for (const p of items) {
    const x = par.get(p.famille) || { nombre: 0, tonnes: 0 }
    x.nombre += 1
    x.tonnes += p.tonnes ?? 0
    par.set(p.famille, x)
  }
  const prises = new Set<string>()
  const parTonnes = [...items].sort((a, b) => (b.tonnes ?? 0) - (a.tonnes ?? 0))
  return [...par.entries()]
    .sort((a, b) => b[1].nombre - a[1].nombre || b[1].tonnes - a[1].tonnes)
    .map(([f, x]) => {
      const p = parTonnes.find((q) => q.famille === f && q.photo && !prises.has(q.photo.url))
      if (p?.photo) prises.add(p.photo.url)
      return { famille: f, nombre: x.nombre, tonnes: Math.round(x.tonnes), libelle: libelleFamille(f, x.nombre), couleur: COULEUR_FAMILLE[f], photo: p?.photo ?? null }
    })
}

/* ═══════ Mosaïque ═══════ */

/** Nombre de vignettes de la mosaïque des projets. */
export const VIGNETTES_MAX = 12

/**
 * Mosaïque des photos des projets : les plus gros volumes de l'entreprise
 * d'abord, une photo jamais répétée (plusieurs projets peuvent partager la
 * photo générique de leur méthode), douze au plus. Vide sous quatre photos.
 */
export function mosaique(items: ProjetExplorateur[], max = VIGNETTES_MAX): ProjetExplorateur[] {
  const vues = new Set<string>()
  const out: ProjetExplorateur[] = []
  for (const p of [...items].sort((a, b) => (b.tonnes ?? 0) - (a.tonnes ?? 0))) {
    if (!p.photo || vues.has(p.photo.url)) continue
    vues.add(p.photo.url)
    out.push(p)
    if (out.length >= max) break
  }
  return out.length >= 4 ? out : []
}

/* ═══════ Filtres ═══════ */

export interface FiltresExplorateur {
  recherche: string
  region: string | null
  /** `famille:<famille>` ou `methode:<type>` */
  methode: string | null
  annee: number | null
}

export const FILTRES_VIDES: FiltresExplorateur = { recherche: '', region: null, methode: null, annee: null }

export const filtreActif = (f: FiltresExplorateur) => Boolean(f.recherche.trim() || f.region || f.methode || f.annee)

/** Sans accents, sans casse, tirets et apostrophes ramenés à des espaces. */
export const normaliserRecherche = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[-’'_.,]/g, ' ').replace(/\s+/g, ' ').trim()

export function correspond(p: ProjetExplorateur, f: FiltresExplorateur): boolean {
  if (f.region && p.region !== f.region) return false
  if (f.annee && p.annee !== f.annee) return false
  if (f.methode) {
    const [genre, valeur] = f.methode.split(':')
    if (genre === 'famille' && p.famille !== valeur) return false
    if (genre === 'methode' && p.type !== valeur) return false
  }
  const q = normaliserRecherche(f.recherche)
  if (q) {
    const botte = normaliserRecherche([p.titre, p.commune, p.departement].filter(Boolean).join(' '))
    if (!q.split(' ').every((mot) => botte.includes(mot))) return false
  }
  return true
}

export const filtrerProjets = (items: ProjetExplorateur[], f: FiltresExplorateur) => items.filter((p) => correspond(p, f))

/** « 12 projets sur 68 », « 1 projet sur 68 », « Aucun projet sur 68 ». */
export function compteur(n: number, total: number): string {
  const t = total.toLocaleString('fr-FR')
  if (n === 0) return `Aucun projet sur ${t}`
  return `${n.toLocaleString('fr-FR')} ${n > 1 ? 'projets' : 'projet'} sur ${t}`
}

export interface OptionFiltre { valeur: string; libelle: string; nombre: number }

export interface OptionsFiltres {
  regions: OptionFiltre[]
  familles: OptionFiltre[]
  methodes: OptionFiltre[]
  annees: OptionFiltre[]
}

function compter<T>(items: ProjetExplorateur[], cle: (p: ProjetExplorateur) => T | null): Map<T, number> {
  const m = new Map<T, number>()
  for (const p of items) {
    const k = cle(p)
    if (k === null || k === undefined || k === '') continue
    m.set(k, (m.get(k) || 0) + 1)
  }
  return m
}

/** Valeurs proposées par chaque filtre, avec leur nombre de projets — seulement celles qui existent. */
export function optionsFiltres(items: ProjetExplorateur[]): OptionsFiltres {
  const alpha = (a: OptionFiltre, b: OptionFiltre) => a.libelle.localeCompare(b.libelle, 'fr')
  const regions = [...compter(items, (p) => p.region || null)].map(([v, n]) => ({ valeur: v, libelle: v, nombre: n })).sort(alpha)
  const familles = [...compter(items, (p) => p.famille)]
    .sort((a, b) => b[1] - a[1])
    .map(([f, n]) => ({ valeur: `famille:${f}`, libelle: NOMS_FAMILLE[f].filtre, nombre: n }))
  const libelles = new Map(items.map((p) => [p.type, p.methode]))
  const methodes = [...compter(items, (p) => p.type)].map(([t, n]) => ({ valeur: `methode:${t}`, libelle: libelles.get(t) || t, nombre: n })).sort(alpha)
  const annees = [...compter(items, (p) => p.annee)].sort((a, b) => b[0] - a[0]).map(([a, n]) => ({ valeur: String(a), libelle: String(a), nombre: n }))
  return { regions, familles, methodes, annees }
}

/* ═══════ Sélection ═══════ */

/** Projet affiché à l'arrivée : le projet principal, sinon le premier de la liste. */
export function projetParDefaut(items: ProjetExplorateur[]): string | null {
  return (items.find((p) => p.enAvant) ?? items[0])?.slug ?? null
}

/** Ancre de la section d'un projet, la même qu'avant l'explorateur. */
export const ancreProjet = (slug: string) => `projet-${slug}`

/** `#projet-<slug>` → slug, s'il désigne un projet de la page ; sinon null. */
export function slugDepuisAncre(ancre: string | null | undefined, items: { slug: string }[]): string | null {
  if (!ancre) return null
  let brut = ancre.replace(/^#/, '')
  try { brut = decodeURIComponent(brut) } catch { /* ancre mal formée : prise telle quelle */ }
  if (!brut.startsWith('projet-')) return null
  const slug = brut.slice('projet-'.length)
  return items.some((p) => p.slug === slug) ? slug : null
}

/**
 * Position du projet affiché et ses voisins, dans l'ordre de la liste
 * filtrée quand le projet en fait partie, sinon dans la liste complète.
 */
export function voisins(items: { slug: string }[], filtres: { slug: string }[], slug: string) {
  const ordre = filtres.some((p) => p.slug === slug) ? filtres : items
  const i = ordre.findIndex((p) => p.slug === slug)
  return {
    position: i + 1,
    total: ordre.length,
    precedent: i > 0 ? ordre[i - 1]!.slug : null,
    suivant: i >= 0 && i < ordre.length - 1 ? ordre[i + 1]!.slug : null,
  }
}
